import { getInputFormat, ReadFileSystem } from '@playcanvas/splat-transform';
import { AppBase, Asset, GSplatResource, Model, Vec3 } from 'playcanvas';

import { Events } from './events';
import { loadGSplatData, validateGSplatData } from './io';
import { Mesh } from './mesh';
import { buildModelFromObj } from './obj-importer';
import { parseObj } from './obj-parser';
import { Splat } from './splat';

const getOrientation = (filename: string) => {
    switch (getInputFormat(filename)) {
        case 'spz':
            return new Vec3(0, 0, 0);
        case 'lcc':
            return new Vec3(90, 0, 180);
        default:
            return new Vec3(0, 0, 180);
    }
};

// handles loading gsplat assets using splat-transform
class AssetLoader {
    app: AppBase;
    events: Events;

    constructor(app: AppBase, events: Events) {
        this.app = app;
        this.events = events;
    }

    private getLocalBlob(fileSystem: ReadFileSystem, filename: string): Blob | null {
        const fsAny = fileSystem as any;
        if (!fsAny?.getLocalFile) {
            return null;
        }

        const basename = filename.replace(/\\/g, '/').split('/').pop() ?? filename;
        return fsAny.getLocalFile(filename) ?? fsAny.getLocalFile(basename) ?? null;
    }

    private async loadGltf(filename: string, fileSystem: ReadFileSystem): Promise<Mesh> {
        this.events.fire('progressStart', 'Loading mesh...');

        const tempUrls: string[] = [];
        const registerTempUrl = (blob: Blob) => {
            const url = URL.createObjectURL(blob);
            tempUrls.push(url);
            return url;
        };

        const revokeTempUrls = () => {
            tempUrls.forEach((url) => {
                try {
                    URL.revokeObjectURL(url);
                } catch (e) {
                    // ignore
                }
            });
        };

        try {
            const lower = filename.toLowerCase();
            const localBlob = this.getLocalBlob(fileSystem, filename);

            let loadUrl = filename;

            if (localBlob) {
                if (lower.endsWith('.gltf')) {
                    const text = await localBlob.text();
                    const gltf = JSON.parse(text);

                    const remapUri = (uri: string) => {
                        if (!uri || uri.startsWith('data:') || /^https?:/i.test(uri) || uri.startsWith('blob:')) {
                            return uri;
                        }

                        const candidates = [
                            uri,
                            decodeURIComponent(uri),
                            uri.replace(/^\.\//, ''),
                            decodeURIComponent(uri.replace(/^\.\//, '')),
                            uri.replace(/\\/g, '/').split('/').pop() ?? uri
                        ];

                        for (const candidate of candidates) {
                            const blob = this.getLocalBlob(fileSystem, candidate);
                            if (blob) {
                                return registerTempUrl(blob);
                            }
                        }

                        return uri;
                    };

                    if (Array.isArray(gltf.buffers)) {
                        gltf.buffers.forEach((buffer: any) => {
                            if (typeof buffer?.uri === 'string') {
                                buffer.uri = remapUri(buffer.uri);
                            }
                        });
                    }

                    if (Array.isArray(gltf.images)) {
                        gltf.images.forEach((image: any) => {
                            if (typeof image?.uri === 'string') {
                                image.uri = remapUri(image.uri);
                            }
                        });
                    }

                    const patched = new Blob([JSON.stringify(gltf)], { type: 'model/gltf+json' });
                    loadUrl = registerTempUrl(patched);
                } else {
                    // .glb local file
                    loadUrl = registerTempUrl(localBlob);
                }
            }

            const asset = new Asset(filename, 'container', {
                url: loadUrl,
                filename
            });

            await new Promise<void>((resolve, reject) => {
                asset.once('load', () => resolve());
                asset.once('error', (err: string) => reject(new Error(`Failed to load GLTF container: ${err}`)));
                this.app.assets.add(asset);
                this.app.assets.load(asset);
            });

            const meshElement = new Mesh(asset);
            if (localBlob) {
                (meshElement as any)._sourceMeshFile = localBlob;
                (meshElement as any)._sourceMeshName = filename;
            }

            return meshElement;
        } finally {
            revokeTempUrls();
            this.events.fire('progressEnd');
        }
    }

    private async loadObj(filename: string, fileSystem: ReadFileSystem): Promise<Mesh> {
        this.events.fire('progressStart', 'Loading mesh...');

        try {
            const localBlob = this.getLocalBlob(fileSystem, filename);

            if (localBlob) {
                const objText = await localBlob.text();
                const parsed = parseObj(objText);
                const model: Model = buildModelFromObj(this.app, parsed);

                const modelAsset = new Asset(filename, 'model', { filename });
                modelAsset.resource = model as any;
                this.app.assets.add(modelAsset);

                const meshElement = new Mesh(modelAsset);
                (meshElement as any)._sourceMeshFile = localBlob;
                (meshElement as any)._sourceMeshName = filename;
                return meshElement;
            }

            const asset = new Asset(filename, 'model', {
                url: filename,
                filename
            });

            await new Promise<void>((resolve, reject) => {
                asset.once('load', () => resolve());
                asset.once('error', (err: string) => reject(new Error(`Failed to load OBJ model: ${err}`)));
                this.app.assets.add(asset);
                this.app.assets.load(asset);
            });

            return new Mesh(asset);
        } finally {
            this.events.fire('progressEnd');
        }
    }

    async load(filename: string, fileSystem: ReadFileSystem, animationFrame?: boolean, skipReorder?: boolean) {
        const lower = filename.toLowerCase();

        if (lower.endsWith('.gltf') || lower.endsWith('.glb')) {
            return this.loadGltf(filename, fileSystem);
        }

        if (lower.endsWith('.obj')) {
            return this.loadObj(filename, fileSystem);
        }

        if (!animationFrame) {
            this.events.fire('startSpinner');
        }

        try {
            // Skip reordering for animation frames (speed) or when explicitly requested (already ordered)
            const gsplatData = await loadGSplatData(filename, fileSystem, skipReorder || animationFrame);
            validateGSplatData(gsplatData);

            const asset = new Asset(filename, 'gsplat', { url: `local-asset-${Date.now()}`, filename });
            this.app.assets.add(asset);
            asset.resource = new GSplatResource(this.app.graphicsDevice, gsplatData);

            return new Splat(asset, getOrientation(filename));
        } finally {
            if (!animationFrame) {
                this.events.fire('stopSpinner');
            }
        }
    }
}

export { AssetLoader };
